import { useState, useEffect, useCallback } from 'react';
import { isAPIError, type QueuedMessage } from '@localagent/sdk';
import type { ExecutionState } from '../../hooks/useSessionEvents';
import { useQueuedImageThumbnail } from '../../hooks/useQueuedImageThumbnail';

interface ExecutionControlBarProps {
  sessionId: string | undefined;
  executionState: ExecutionState;
  queue: QueuedMessage[];
  onStop: () => void;
  onRemoveQueueItem: (id: string) => Promise<void>;
  onClearQueue: () => Promise<void>;
  onMoveQueueItem: (id: string, direction: 'up' | 'down') => Promise<void>;
  onEditQueueItem: (id: string, content: string) => Promise<void>;
  onRedirect?: () => void;
  inputHasText?: boolean;
  // (sessions ADR-2a41a)
  backgroundRunning?: boolean;
  onStopAll?: () => void;
  scope?: 'main' | 'participant';
  // (agents ADR-14bdc)
  waitingOnUser?: boolean;
  onOpenQuestions?: () => void;
}

function blockedReasonText(reason: QueuedMessage['blockedReason']): string {
  if (reason === 'admission') return 'Waiting for the workspace';
  if (reason === 'image_expired') return 'Image needs re-attaching';
  throw new Error(`a blocked queue item carries no reason`);
}

// (sessions ADR-d28b1)
function queueActionError(err: unknown): string {
  if (isAPIError(err) && err.code === 'queue_item_gone') return 'This message already started';
  return err instanceof Error ? err.message : String(err);
}

function authorLine(m: QueuedMessage): string | null {
  const original = m.originalAuthorLabel ?? 'another member';
  const author = m.authorLabel ?? 'another member';
  if (m.originalAuthorUserId !== null && m.authorUserId !== m.originalAuthorUserId) {
    return `queued by ${original}, edited by ${author}`;
  }
  if (m.authorIsViewer || m.authorUserId === null) return null;
  return `queued by ${author}`;
}

function QueuedImageThumb({ sessionId, imageId, label }: { sessionId: string | undefined; imageId: string; label: string }) {
  const thumb = useQueuedImageThumbnail(sessionId, imageId);
  if (thumb.state === 'ready') {
    return (
      <img
        data-testid={`queue-image-thumb-${imageId}`}
        src={thumb.url}
        alt={label}
        className="h-10 w-10 rounded border border-border object-cover"
      />
    );
  }
  if (thumb.state === 'missing') {
    return (
      <span data-testid={`queue-image-unavailable-${imageId}`} className="block text-destructive">
        Image not available on this device — remove and re-attach
      </span>
    );
  }
  if (thumb.state === 'error') {
    return (
      <span data-testid={`queue-image-error-${imageId}`} className="block text-destructive">
        Couldn't read this image on this device: {thumb.message}
      </span>
    );
  }
  return null;
}

export function ExecutionControlBar({
  sessionId,
  executionState,
  queue,
  onStop,
  onRemoveQueueItem,
  onClearQueue,
  onMoveQueueItem,
  onEditQueueItem,
  onRedirect,
  inputHasText,
  backgroundRunning = false,
  onStopAll,
  scope = 'main',
  waitingOnUser = false,
  onOpenQuestions,
}: ExecutionControlBarProps) {
  const queueCount = queue.length;
  const [expanded, setExpanded] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [editing, setEditing] = useState<QueuedMessage | null>(null);
  const editingId = editing?.id ?? null;
  const rows = editing && !queue.some((m) => m.id === editing.id) ? [...queue, editing] : queue;
  const [draft, setDraft] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (rows.length === 0) {
      setExpanded(false);
      setSelectedIndex(null);
    }
  }, [rows.length]);

  useEffect(() => {
    if (selectedIndex !== null && selectedIndex >= queueCount) {
      setSelectedIndex(queueCount > 0 ? queueCount - 1 : null);
    }
  }, [queueCount, selectedIndex]);

  const run = useCallback((action: Promise<void>) => {
    setActionError(null);
    action.catch((err: unknown) => setActionError(queueActionError(err)));
  }, []);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!expanded || queueCount === 0 || editingId !== null) return;

    const ctrlOrMeta = e.ctrlKey || e.metaKey;

    if (e.key === 'Escape') {
      e.preventDefault();
      setSelectedIndex(null);
      return;
    }

    if (e.key === 'ArrowDown' && !ctrlOrMeta) {
      e.preventDefault();
      setSelectedIndex(prev => (prev === null ? 0 : Math.min(prev + 1, queueCount - 1)));
      return;
    }

    if (e.key === 'ArrowUp' && !ctrlOrMeta) {
      e.preventDefault();
      setSelectedIndex(prev => (prev === null ? queueCount - 1 : Math.max(prev - 1, 0)));
      return;
    }

    if (selectedIndex === null) return;

    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      run(onRemoveQueueItem(queue[selectedIndex].id));
      return;
    }

    if (e.key === 'ArrowDown' && ctrlOrMeta && selectedIndex < queueCount - 1) {
      e.preventDefault();
      run(onMoveQueueItem(queue[selectedIndex].id, 'down'));
      setSelectedIndex(selectedIndex + 1);
      return;
    }

    if (e.key === 'ArrowUp' && ctrlOrMeta && selectedIndex > 0) {
      e.preventDefault();
      run(onMoveQueueItem(queue[selectedIndex].id, 'up'));
      setSelectedIndex(selectedIndex - 1);
    }
  }, [expanded, queueCount, editingId, selectedIndex, queue, onRemoveQueueItem, onMoveQueueItem, run]);

  useEffect(() => {
    if (!expanded) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [expanded, handleKeyDown]);

  const offerStopAll = backgroundRunning && onStopAll !== undefined;
  const turnRunning = executionState === 'running';
  if (!turnRunning && !offerStopAll && rows.length === 0) return null;

  const saveEdit = (id: string) => {
    const text = draft.trim();
    if (!text) return;
    setActionError(null);
    onEditQueueItem(id, text).then(
      () => setEditing(null),
      (err: unknown) => setActionError(queueActionError(err)),
    );
  };

  return (
    <div data-testid="execution-control-bar" className="border-t border-border bg-muted/30 px-4 py-2 space-y-2">
      {(turnRunning || offerStopAll) && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-body">
            {!turnRunning && (
              <span data-testid="execution-status-background" className="text-muted-foreground">
                Background work is running
              </span>
            )}
            {turnRunning && waitingOnUser && (
              <button
                type="button"
                data-testid="execution-status-waiting"
                onClick={onOpenQuestions}
                className="flex items-center gap-2 text-info-text underline-offset-2 hover:underline"
              >
                <span className="relative inline-flex rounded-full h-2 w-2 bg-info" />
                <span>Waiting for your answer</span>
              </button>
            )}
            {turnRunning && !waitingOnUser && (
              <span data-testid="execution-status-running" className="flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-success" />
                </span>
                <span className="text-muted-foreground">Agent is working...</span>
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            {executionState === 'running' && inputHasText && onRedirect && (
              <button
                data-testid="redirect-button"
                onClick={onRedirect}
                className="rounded-md px-2.5 py-1 text-caption font-medium bg-info-subtle text-info-text hover:bg-info/20 transition-colors"
              >
                Redirect
              </button>
            )}
            {turnRunning && (
              <button
                data-testid="stop-button"
                onClick={onStop}
                className="rounded-md px-2.5 py-1 text-caption font-medium bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors"
              >
                {scope === 'participant' ? 'Stop this turn' : 'Stop turn'}
              </button>
            )}
            {offerStopAll && (
              <button
                data-testid="stop-all-button"
                onClick={onStopAll}
                className="rounded-md px-2.5 py-1 text-caption font-medium bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors"
              >
                {scope === 'participant' ? "Stop this agent's work" : 'Stop all'}
              </button>
            )}
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <button
              data-testid="queue-expand-toggle"
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 text-caption text-muted-foreground hover:text-foreground transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`transition-transform ${expanded ? 'rotate-90' : ''}`}
              >
                <path d="m9 18 6-6-6-6" />
              </svg>
              <span data-testid="queue-count">{queueCount} message{queueCount !== 1 ? 's' : ''} queued</span>
            </button>
            <button
              data-testid="clear-queue-button"
              onClick={() => run(onClearQueue())}
              className="text-caption text-muted-foreground hover:text-foreground transition-colors"
            >
              Clear
            </button>
          </div>
          {actionError && (
            <div data-testid="queue-error" role="alert" className="text-caption text-destructive">
              {actionError}
            </div>
          )}
          {expanded && (
            <div data-testid="queue-list" className="space-y-0.5">
              {rows.map((m, index) => {
                const byline = authorLine(m);
                const started = index >= queue.length;
                return (
                  <div
                    key={m.id}
                    data-testid={`queue-item-${m.id}`}
                    data-status={m.status}
                    aria-selected={selectedIndex === index}
                    onClick={() => setSelectedIndex(index)}
                    className={`flex items-start gap-1.5 text-caption text-muted-foreground bg-background rounded px-2 py-1 cursor-pointer transition-colors ${selectedIndex === index ? 'ring-1 ring-primary bg-primary/5' : 'hover:bg-accent/50'}`}
                  >
                    <div className="flex-1 min-w-0 space-y-0.5">
                      {editingId === m.id ? (
                        <div className="space-y-1" onClick={(e) => e.stopPropagation()}>
                          <textarea
                            data-testid={`queue-item-edit-input-${m.id}`}
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            className="w-full rounded border border-border bg-background px-1.5 py-1 text-caption text-foreground"
                            rows={3}
                          />
                          <div className="flex gap-1.5">
                            <button
                              data-testid={`queue-item-edit-save-${m.id}`}
                              onClick={() => saveEdit(m.id)}
                              className="rounded px-2 py-0.5 text-caption font-medium bg-primary text-primary-foreground"
                            >
                              Save
                            </button>
                            <button
                              data-testid={`queue-item-edit-cancel-${m.id}`}
                              onClick={() => { setEditing(null); setActionError(null); }}
                              className="rounded px-2 py-0.5 text-caption text-muted-foreground hover:text-foreground"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <span data-testid={`queue-item-text-${m.id}`} className="block whitespace-pre-wrap break-words">
                          {m.content}
                        </span>
                      )}
                      {byline && (
                        <span data-testid={`queue-item-author-${m.id}`} className="block text-muted-foreground/70">
                          {byline}
                        </span>
                      )}
                      {m.attachments.some((a) => a.type === 'image') && (
                        <div className="flex flex-wrap gap-1">
                          {m.attachments.map((a) =>
                            a.type === 'image' ? (
                              <QueuedImageThumb key={a.image_id} sessionId={sessionId} imageId={a.image_id} label={a.label} />
                            ) : null,
                          )}
                        </div>
                      )}
                      {m.status === 'blocked' && (
                        <span
                          data-testid={`queue-item-blocked-${m.id}`}
                          data-blocked-reason={m.blockedReason ?? undefined}
                          className={`block ${m.blockedReason === 'admission' ? 'text-muted-foreground' : 'text-destructive'}`}
                        >
                          {blockedReasonText(m.blockedReason)}
                        </span>
                      )}
                    </div>
                    {editingId !== m.id && (
                      <button
                        data-testid={`queue-item-edit-${m.id}`}
                        onClick={(e) => { e.stopPropagation(); setDraft(m.content); setEditing(m); }}
                        className="text-muted-foreground/50 hover:text-foreground shrink-0"
                        aria-label="Edit"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                      </button>
                    )}
                    <button
                      data-testid={`queue-item-up-${m.id}`}
                      onClick={(e) => { e.stopPropagation(); if (index > 0) run(onMoveQueueItem(m.id, 'up')); }}
                      disabled={started || index === 0}
                      className="text-muted-foreground/50 hover:text-foreground shrink-0 disabled:opacity-30"
                      aria-label="Move up"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m18 15-6-6-6 6"/></svg>
                    </button>
                    <button
                      data-testid={`queue-item-down-${m.id}`}
                      onClick={(e) => { e.stopPropagation(); if (index < queue.length - 1) run(onMoveQueueItem(m.id, 'down')); }}
                      disabled={started || index === queue.length - 1}
                      className="text-muted-foreground/50 hover:text-foreground shrink-0 disabled:opacity-30"
                      aria-label="Move down"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
                    </button>
                    <button
                      data-testid={`queue-item-remove-${m.id}`}
                      onClick={(e) => { e.stopPropagation(); run(onRemoveQueueItem(m.id)); }}
                      disabled={started}
                      className="text-muted-foreground/50 hover:text-foreground shrink-0 disabled:opacity-30"
                      aria-label="Remove"
                    >
                      &times;
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
